/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { CategoryHierarchy } from '@msdyn365-commerce/commerce-entities';
import MsDyn365, { IActionContext, IGridSettings, IImageSettings, isEmptyOrNullObject } from '@msdyn365-commerce/core';
import { TextValueTranslation } from '@msdyn365-commerce/retail-proxy';
import { getCategoryUrl } from '@msdyn365-commerce-modules/retail-actions';
import { addThrottledEvent, IModuleProps, INodeProps } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { computed, observable } from 'mobx';
import * as React from 'react';
import NavigationMenuView from './dobbies-navigation-menu.view';
import { IMenuItemData, IDobbiesNavigationMenuData } from './dobbies-navigation-menu.data';
import {
    ICmsNavItemsData,
    IDobbiesNavigationMenuProps,
    ISubMenusData,
    navigationMenuSource
} from './dobbies-navigation-menu.props.autogenerated';
import { setLocalStorage } from '../../themes/dobbies/utilities/storage';

export interface INavigationMenuViewProps extends IDobbiesNavigationMenuProps<IDobbiesNavigationMenuData> {
    className: string;
    menuItemData: IMenuItemData[];
    Navigation: IModuleProps;
    MenuList: INodeProps;
    ListItem: INodeProps;
    Link: INodeProps;
    Button: INodeProps;
    Span: INodeProps;
    ImageContainer: INodeProps;
    MobileDescriptionContainer: INodeProps;
    MobileBackButton: INodeProps;
    MobileDescriptionLabel: INodeProps;
    imageSettings: IImageSettings;
    gridSettings: IGridSettings;
    isMobileView: boolean;
    DivContainer?: INodeProps;
    ImageDivContainer?: INodeProps;
    showCategoryImage?: boolean;
    showPromotionalContent?: boolean;
}

export type GridSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export interface INavigationMenuState {
    isMobile: boolean;
    navWidthStandard?: number;
    navLeftPadding?: number;
    navRightPadding?: number;
    navMarginLeft?: number;
    navMarginRight?: number;
}

/**
 *
 * NavigationMenu component.
 * @extends {React.PureComponent<IDobbiesNavigationMenuProps<IDobbiesNavigationMenuData>>}
 */
class NavigationMenu extends React.PureComponent<IDobbiesNavigationMenuProps<IDobbiesNavigationMenuData>, INavigationMenuState> {
    // private static CMSItemIdInit: number = MenuTypeIndexId.Cms;

    // private static RSItemIdInit: number = MenuTypeIndexId.Retail;

    private resizeThrottledEventHandler?: (event: Event) => void;

    private menuItemList: IMenuItemData[];

    private isRetail: boolean = true;

    private isCMS: boolean = true;

    // private lastKnownNavHeight: number = 0;

    @observable
    private viewport: GridSize;

    public static getFriendlyName(locale: string, nameTranslations?: TextValueTranslation[]): string | undefined {
        let nameTranslation: TextValueTranslation | undefined;
        if (locale && nameTranslations && nameTranslations.length > 0) {
            nameTranslation = nameTranslations.find(item => item.Language!.toLowerCase() === locale.toLowerCase());
        }

        return nameTranslation && nameTranslation.Text;
    }

    constructor(props: IDobbiesNavigationMenuProps<IDobbiesNavigationMenuData>) {
        super(props);
        this._updateViewport = this._updateViewport.bind(this);
        this.viewport =
            props.context.request && props.context.request.device && props.context.request.device.Type === 'Mobile' ? 'xs' : 'lg';
        this._setNavigationSource(this.props.config && this.props.config.navigationMenuSource);
        this.state = { isMobile: this.isMobile };
        this.menuItemList = [];
    }

    @computed get isMobile(): boolean {
        return this.viewport === 'xs' || this.viewport === 'sm' || this.isNavOutOfBounds();
    }

    public componentDidMount(): void {
        if (MsDyn365.isBrowser) {
            this.resizeThrottledEventHandler = addThrottledEvent(window, 'resize', this._updateViewport as EventListener);
        }
        this._updateViewport();
        if (this.props.config.subMenuStaticMenus && this.props.config.subMenuStaticMenus.length) {
            setLocalStorage('static_submenu_items', JSON.stringify(this.props.config.subMenuStaticMenus));
        }
    }

    public componentWillUnmount(): void {
        if (MsDyn365.isBrowser) {
            window.removeEventListener('resize', this.resizeThrottledEventHandler!, false);
        }
    }

    public render(): JSX.Element | null {
        this.menuItemList = this._mapData();
        if (!this.state.navWidthStandard && !this.state.isMobile) {
            this.setNavWidthStandard();
        }
        if (isEmptyOrNullObject(this.props.data)) {
            this.props.telemetry.error('Navigatiomenu returned null');
            return null;
        }
        const defaultImageSettings: IImageSettings = {
            viewports: {
                xs: { q: 'w=224&h=300&m=6', w: 0, h: 0 },
                xl: { q: 'w=224&h=300&m=6', w: 0, h: 0 }
            },
            lazyload: true
        };
        const viewProps = {
            ...this.props,
            ...this.state,
            imageSettings: defaultImageSettings,
            gridSettings: this.props.context.request.gridSettings,
            Navigation: {
                moduleProps: this.props,
                className: classnames('ms-nav', this.props.config.className, this.state.isMobile ? 'mobile-vp' : 'desktop-vp'),
                tag: 'nav',
                role: this.isMobile ? 'listbox' : 'navigation'
            },
            MenuList: {
                className: 'ms-nav__list',
                tag: 'ul',
                role: this.isMobile ? 'listbox' : 'menu'
            },
            ListItem: {
                className: 'ms-nav__list__item',
                role: 'presentation',
                tag: 'li'
            },
            showCategoryImage: this.props.config.displayCategoryImage ?? false,
            showPromotionalContent: this.props.config.displayPromotionalImage ?? false,
            Link: {
                tag: 'a',
                className: 'ms-nav__list__item__link',
                role: this.isMobile ? 'option' : 'menuitem'
            },
            DivContainer: {
                tag: 'div',
                className: 'ms-nav__feature'
            },
            ImageDivContainer: {
                tag: 'div',
                className: 'category-image',
                role: 'tabpanel'
            },
            Button: {
                className: 'ms-nav__list__item__button',
                tag: 'button',
                role: this.isMobile ? 'option' : 'menuitem',
                onClick: this.props
            },
            ImageContainer: {
                className: 'ms-nav__list__item__image'
            },
            Span: {
                className: 'ms-nav__list__item__span',
                tag: 'span'
            },
            MobileDescriptionContainer: {
                className: 'ms-nav__list__mobile__container'
            },
            MobileBackButton: {
                className: 'ms-nav__list__mobile__container__button',
                tag: 'button',
                'aria-label': this.props.resources.backButtonAriaLabel,
                onClick: this.props
            },
            MobileDescriptionLabel: {
                className: 'ms-nav__list__mobile__container__span',
                tag: 'span'
            },
            isMobileView:
                this.state.isMobile ||
                (this.props.context.request &&
                    this.props.context.request.device &&
                    (this.props.context.request.device.Type as DeviceType) === 'Mobile'),
            menuItemData: this.menuItemList,
            className: classnames('ms-nav', this.props.config.className)
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    // private _mapData(): IMenuItemData[] {
    //     const { data, config } = this.props;
    //     const mergedData: IMenuItemData[] = [];
    //     if (this.isRetail && data && data.categories && data.categories.result && data.categories.result.length > 0) {
    //         data.categories.result.map((item: CategoryHierarchy, index) =>
    //             mergedData.push(this._constructMenuItemData(item, undefined, true, index, true))
    //         );
    //     }

    //     if (this.isCMS && config.cmsNavItems && config.cmsNavItems.length > 0) {
    //         config.cmsNavItems.map((item: ICmsNavItemsData, idx: number) =>
    //             mergedData.push(this._constructMenuItemData(undefined, item, false, idx + 1))
    //         );
    //     }
    //     return this._constructTopMenuItemData(mergedData);
    // }

    // private _constructTopMenuItemData(mergedData: IMenuItemData[]): IMenuItemData[] {
    //     const enableTopLevelMenu = this.props.config.enabletopMenu ?? false;
    //     if (!enableTopLevelMenu) {
    //         return mergedData;
    //     }
    //     const mergedRootMenuData: IMenuItemData[] = [];
    //     const topMenu = {
    //         id: 1,
    //         linkText: this.props.config.rootMenuNavigation ?? '...',
    //         linkURL: '#',
    //         imageSource: undefined,
    //         subMenu: mergedData
    //     } as IMenuItemData;
    //     mergedRootMenuData.push(topMenu);

    //     return mergedRootMenuData;
    // }

    // private _constructMenuItemData(
    //     retailItem?: CategoryHierarchy,
    //     cmsItem?: ICmsNavItemsData,
    //     isRetail?: boolean,
    //     idx?: number,
    //     addedAllMenu?: boolean
    // ): IMenuItemData {
    //     const locale =
    //         this.props.context && this.props.context.request && this.props.context.request.locale
    //             ? this.props.context.request.locale
    //             : 'en-us';
    //     const { categoryPromotionalContent } = this.props.config;
    //     const cmsItemText = (cmsItem && cmsItem.linkText) || '';
    //     const { menuAriaLabel } = this.props.resources;

    //     // Filter category promotional data based on category name
    //     const promotionalData =
    //         retailItem &&
    //         categoryPromotionalContent?.filter(promoItem => promoItem.categoryName?.toLowerCase() === retailItem?.Name?.toLowerCase());

    //     // First check if image is available from promotional data else read from retail server
    //     let imageSource: string = 'empty';
    //     if (ArrayExtensions.hasElements(retailItem?.Images) && retailItem!.Images[0].Uri) {
    //         imageSource = retailItem!.Images[0].Uri;
    //     }
    //     if (isRetail) {
    //         return {
    //             id: NavigationMenu.RSItemIdInit++,
    //             linkText: NavigationMenu.getFriendlyName(locale, retailItem?.NameTranslations) ?? '',
    //             linkURL: retailItem?.Url,
    //             imageSource,
    //             subMenu: this._getNestedChildren(retailItem, imageSource, addedAllMenu),
    //             promotionalContent: this.props.config.displayPromotionalImage && promotionalData,
    //             menuSource: MenuSource.Retail,
    //             ariaLabel: `${NavigationMenu.getFriendlyName(locale, retailItem?.NameTranslations) ?? ''} ${menuAriaLabel}`
    //         } as IMenuItemData;
    //     }
    //     return {
    //         id: NavigationMenu.CMSItemIdInit++,
    //         linkText: cmsItemText,
    //         linkURL: (cmsItem && cmsItem.linkUrl && cmsItem.linkUrl.destinationUrl) || '',
    //         imageDestinationURL: (cmsItem && cmsItem.imageLink && cmsItem.imageLink.destinationUrl) || '',
    //         imageSource: (cmsItem && cmsItem.image && cmsItem.image.src) || '',
    //         subMenu: this._getCMSNestedChildren(cmsItem),
    //         menuSource: MenuSource.Cms,
    //         shouldOpenNewTab: cmsItem?.openInNewTab,
    //         ariaLabel: `${cmsItemText} ${menuAriaLabel}`
    //     } as IMenuItemData;
    // }

    // private _getNestedChildren(category?: CategoryHierarchy, imageSourceUrl?: string, addedAllMenu: boolean = false): IMenuItemData[] {
    //     const nestedChildren: IMenuItemData[] = [];
    //     if (!category || !category.Children || category.Children.length === 0) {
    //         return [];
    //     }
    //     if (!this.props.config.enabletopMenu && addedAllMenu) {
    //         nestedChildren.push({
    //             linkText: this.props.resources.allCategoryMenuText,
    //             imageSource: imageSourceUrl,
    //             linkURL: getCategoryUrl(category, (this.props.context as unknown) as IActionContext, {})
    //         });
    //     }
    //     category.Children.map((item: CategoryHierarchy, index) => {
    //         nestedChildren.push(this._constructMenuItemData(item, undefined, true, index, false));
    //     });
    //     return nestedChildren;
    // }

    // private _getCMSNestedChildren(category?: ICmsNavItemsData): IMenuItemData[] {
    //     const nestedChildren: IMenuItemData[] = [];

    //     if (!category || !category.subMenus || category.subMenus.length === 0) {
    //         return [];
    //     }

    //     category.subMenus.map((item: ISubMenusData, idx: number) => {
    //         nestedChildren.push(this._constructMenuItemData(undefined, item, false, idx));
    //     });
    //     return nestedChildren;
    // }

    ///////// From View ///////////////////////////////
    private _mapData(): IMenuItemData[] {
        const { data, config } = this.props;
        const showCannedSearches = this.props.context.request.query?.showCannedSearches;
        const mergedData: IMenuItemData[] = [];
        /* VSI Customization - START - 8/31/22  /** adding another menu   */
        if (this.isCMS && config.cmsNavItemsNew && config.cmsNavItemsNew.length > 0) {
            config.cmsNavItemsNew.map((item: ICmsNavItemsData, idx: number) =>
                mergedData.push(this._constructMenuItemData(undefined, item, false, idx + 1))
            );
        }
        /* VSI Customization - END - 8/31/22  /** adding another menu   */

        if (
            this.isRetail &&
            config.navigationMenuSource === 'all' &&
            data &&
            data.categories &&
            data.categories.result &&
            data.categories.result.length > 0
        ) {
            const newCategories = this.props.config.removeDomainFromCategoryURLs
                ? this.removeDomainParameter(data.categories.result)
                : data.categories.result;
            newCategories.map((item: CategoryHierarchy, index: number) => {
                // data.categories.result.map((item: CategoryHierarchy, index) => {
                if (
                    item.Name === 'Sale' ||
                    (this.props.config.hideMenuItemPrefix && item.NeutralizedName?.startsWith(this.props.config.hideMenuItemPrefix))
                ) {
                    return;
                }
                (showCannedSearches || !this.shouldHideMenuItem(item.Name)) &&
                    mergedData.push(this._constructMenuItemData(item, undefined, true, index, true));
            });
        }

        if (this.isCMS && config.cmsNavItems && config.cmsNavItems.length > 0) {
            config.cmsNavItems.map((item: ICmsNavItemsData, idx: number) =>
                mergedData.push(this._constructMenuItemData(undefined, item, false, idx + 1))
            );
        }
        return this._constructTopMenuItemData(mergedData);
    }

    private removeDomainParameter(categoriesArray: CategoryHierarchy[]) {
        // Iterate over each category in the array
        categoriesArray.forEach(category => {
            // Check if the current category has a 'Url' property
            if (category.Url && typeof category.Url === 'string') {
                // Remove the '?domain=' parameter and everything after it
                category.Url = category.Url.split('?domain=')[0];
            }
            // If the category has Children, recursively process each child
            if (category.Children && Array.isArray(category.Children)) {
                this.removeDomainParameter(category.Children); // Recursively process children
            }
        });
        return categoriesArray;
    }

    private _constructTopMenuItemData(mergedData: IMenuItemData[]): IMenuItemData[] {
        const enableTopLevelMenu = this.props.config.enabletopMenu || false;
        if (!enableTopLevelMenu) {
            return mergedData;
        }
        const mergedRootMenuData: IMenuItemData[] = [];
        const topMenu = {
            id: 1,
            linkText: this.props.config.rootMenuNavigation || '...',
            linkURL: '#',
            imageSource: undefined,
            subMenu: mergedData
        } as IMenuItemData;
        mergedRootMenuData.push(topMenu);

        return mergedRootMenuData;
    }

    /* VSI CUSTOMIZATION - START CUSTOMIZATION - 08/11/2023 */
    private menuExistsInRedundantMobileMenusConfig(menuText?: string): string {
        if (this.props.config.removeExtraSubMenusOnMobile && this.props.config.removeExtraSubMenusOnMobile.length) {
            for (let i = 0; i < this.props.config.removeExtraSubMenusOnMobile.length; i++) {
                if (menuText === this.props.config.removeExtraSubMenusOnMobile[i]) {
                    return menuText;
                }
            }
        }
        return '';
    }
    /* VSI CUSTOMIZATION - END CUSTOMIZATION - 08/11/2023 */

    // tslint:disable-next-line: cyclomatic-complexity
    private _constructMenuItemData(
        retailItem?: CategoryHierarchy,
        cmsItem?: ICmsNavItemsData,
        isRetail?: boolean,
        idx?: number,
        addedAllMenu?: boolean
    ): IMenuItemData {
        const locale =
            this.props.context && this.props.context.request && this.props.context.request.locale
                ? this.props.context.request.locale
                : 'en-us';
        const imageSouce = retailItem && retailItem.Images && retailItem.Images[0].Uri ? retailItem.Images[0].Uri : 'empty';
        /* VSI CUSTOMIZATION - START - 18/09/2023 */
        const friendlyName = NavigationMenuView.getFriendlyName(locale, retailItem && retailItem.NameTranslations) || '';
        const urlFromConfig = this._getUrlFromConfigOfNavItem(friendlyName);
        if (retailItem?.Name?.toLowerCase().includes('houseplants') || friendlyName.toLowerCase().includes('houseplants')) {
            console.log('Item Details', retailItem);
        }
        if (isRetail) {
            if (retailItem?.Children && retailItem.Children.length) {
                retailItem.Children = retailItem.Children.filter(
                    ric => !(this.props.config.hideMenuItemPrefix && ric.NeutralizedName?.startsWith(this.props.config.hideMenuItemPrefix))
                );
            }
            return {
                id: NavigationMenuView.RSItemIdInit++,
                linkText: friendlyName,
                linkURL: urlFromConfig ? urlFromConfig : retailItem && retailItem.Url,
                imageSource: imageSouce,
                subMenu: this._getNestedChildren(retailItem, imageSouce, addedAllMenu).concat(
                    this._constructSecondLevelStaticMenus(
                        retailItem?.RecordId ? retailItem.RecordId : -1,
                        retailItem?.Name ? retailItem.Name : ''
                    )
                )
            } as IMenuItemData;
        } else {
            const isMobile = this.state.isMobile;
            if (isMobile) {
                const menuText = this.menuExistsInRedundantMobileMenusConfig(cmsItem?.linkText);
                if (menuText) {
                    const subMenu = this._getCMSNestedChildren(cmsItem);
                    if (subMenu.length === 1) {
                        const subMenuItem = subMenu[0];
                        if (subMenuItem.linkText === menuText) {
                            return {
                                id: NavigationMenuView.CMSItemIdInit++,
                                linkText: (cmsItem && cmsItem.linkText) || '',
                                linkURL: (cmsItem && cmsItem.linkUrl && cmsItem.linkUrl.destinationUrl) || '',
                                imageDestinationURL: (cmsItem && cmsItem.imageLink && cmsItem.imageLink.destinationUrl) || '',
                                imageSource: (cmsItem && cmsItem.image && cmsItem.image.src) || '',
                                subMenu: subMenuItem.subMenu
                            } as IMenuItemData;
                        }
                    }
                }
            }
            return {
                id: NavigationMenuView.CMSItemIdInit++,
                linkText: (cmsItem && cmsItem.linkText) || '',
                linkURL: (cmsItem && cmsItem.linkUrl && cmsItem.linkUrl.destinationUrl) || '',
                imageDestinationURL: (cmsItem && cmsItem.imageLink && cmsItem.imageLink.destinationUrl) || '',
                imageSource: (cmsItem && cmsItem.image && cmsItem.image.src) || '',
                subMenu: this._getCMSNestedChildren(cmsItem)
            } as IMenuItemData;
        }
    }

    private _getUrlFromConfigOfNavItem(linkText: string): string {
        var urlFromConfig: string = '';
        if (this.isCMS && this.props.config.subMenuStaticMenus && this.props.config.subMenuStaticMenus.length > 0) {
            this.props.config.subMenuStaticMenus.map((item: ICmsNavItemsData, idx: number) => {
                if (linkText === item.linkText) {
                    urlFromConfig = item.linkUrl?.destinationUrl ? item.linkUrl?.destinationUrl : '';
                }
            });
        }
        return urlFromConfig;
    }

    private _constructSecondLevelStaticMenus(id: number, linkText: string): IMenuItemData[] {
        const staticMenuItems: IMenuItemData[] = [];
        /* VSI Customization - START - 8/31/22  /** adding another menu   */
        if (this.isCMS && this.props.config.subMenuStaticMenus && this.props.config.subMenuStaticMenus.length > 0) {
            this.props.config.subMenuStaticMenus.map((item: ICmsNavItemsData, idx: number) => {
                if (linkText === item.linkText) {
                    item.subMenus?.map(submenu => {
                        staticMenuItems.push(this._constructMenuItemData(undefined, submenu, false, idx + 1));
                    });
                }
            });
        }
        return staticMenuItems;
    }

    private shouldHideMenuItem = (menuName: string | undefined): boolean => {
        const NAMES_TO_HIDE = ['Edits', 'CS-'];
        if (menuName === undefined) {
            return true;
        }
        return NAMES_TO_HIDE.some(name => menuName.startsWith(name));
    };

    private _getNestedChildren(category?: CategoryHierarchy, imageSourceUrl?: string, addedAllMenu: boolean = false): IMenuItemData[] {
        const nestedChildren: IMenuItemData[] = [];
        const showCannedSearches = this.props.context.request.query?.showCannedSearches;
        if (!category || !category.Children || !category.Children.length) {
            return [];
        }
        if (!this.props.config.enabletopMenu && addedAllMenu) {
            const locale =
                this.props.context && this.props.context.request && this.props.context.request.locale
                    ? this.props.context.request.locale
                    : 'en-us';
            const friendlyName = NavigationMenuView.getFriendlyName(locale, category && category.NameTranslations) || '';
            nestedChildren.push({
                linkText: this.props.resources.allCategoryMenuText,
                imageSource: imageSourceUrl,
                linkURL:
                    this._getUrlFromConfigOfNavItem(friendlyName) ||
                    getCategoryUrl(category, (this.props.context as unknown) as IActionContext, {})
            });
        }
        category.Children.map((item: CategoryHierarchy, index) => {
            (showCannedSearches || !this.shouldHideMenuItem(item.Name)) &&
                nestedChildren.push(this._constructMenuItemData(item, undefined, true, index, true));
        });
        return nestedChildren;
    }

    private _getCMSNestedChildren(category?: ICmsNavItemsData): IMenuItemData[] {
        const nestedChildren: IMenuItemData[] = [];

        if (!category || !category.subMenus || !category.subMenus.length) {
            return [];
        }

        category.subMenus.map((item: ISubMenusData, idx: number) => {
            nestedChildren.push(this._constructMenuItemData(undefined, item, false, idx));
        });
        return nestedChildren;
    }
    ///////////////////////////////////////////////////

    private readonly _setNavigationSource = (sourceType?: navigationMenuSource): void => {
        switch (sourceType) {
            case navigationMenuSource.retailServerOnly:
                this.isRetail = true;
                this.isCMS = false;
                break;
            case navigationMenuSource.cmsOnly:
                this.isRetail = false;
                this.isCMS = true;
                break;
            case navigationMenuSource.all:
                this.isRetail = true;
                this.isCMS = true;
                break;
            default:
        }
    };

    private hasClassInParent(element: HTMLElement, className: string): boolean {
        // Start from the parent of the given element
        let parent = element.parentNode as HTMLElement;

        // Traverse up the DOM tree until reaching the body element
        while (parent && parent.tagName !== 'BODY') {
            // Check if the parent has the desired class
            if (parent.classList.contains(className)) {
                return true; // Class found in parent
            }
            // Move up to the next parent element
            parent = parent.parentNode as HTMLElement;
        }

        // If no parent with the class is found, return false
        return false;
    }

    private setNavWidthStandard() {
        if (MsDyn365.isBrowser && window) {
            const navs = document.getElementsByTagName('nav');
            let desktopNav;
            if (navs) {
                for (let i = 0; i < navs.length; i++) {
                    if (
                        !this.hasClassInParent(navs[i], 'ms-header__mobile-hamburger-menu-body') &&
                        !desktopNav?.classList.contains('child') &&
                        !this.hasClassInParent(navs[i], 'ms-search-result-container') &&
                        !this.hasClassInParent(navs[i], 'pdp-breadcrumb') &&
                        !navs[i].classList.contains('basket-breadcrumb') &&
                        navs[i].classList.contains('ms-nav')
                    ) {
                        desktopNav = navs[i];
                    }
                }
            }
            if (desktopNav) {
                let offsetHeight = desktopNav['offsetHeight'] ? desktopNav['offsetHeight'] : 0;
                if (offsetHeight === 0) {
                    desktopNav.classList.remove('mobile-vp');
                    if (!desktopNav.classList.contains('desktop-vp')) {
                        desktopNav.classList.add('desktop-vp');
                    }
                    offsetHeight = desktopNav['offsetHeight'];
                    desktopNav.classList.remove('desktop-vp');
                    if (!desktopNav.classList.contains('mobile-vp')) {
                        desktopNav.classList.add('mobile-vp');
                    }
                }
                const ulDivs = document.getElementsByClassName('ms-nav__list') as any;
                let widthOfWindowIsGreater = false;
                if (ulDivs && ulDivs.length > 0) {
                    if (window && window.innerWidth > ulDivs[0]['offsetWidth']) {
                        widthOfWindowIsGreater = true;
                    }
                }
                if (offsetHeight < 60 || widthOfWindowIsGreater) {
                    const style = window.getComputedStyle(ulDivs[0], null) || (ulDivs[0] as HTMLElement).style;
                    const navPaddingLeft = style.paddingLeft.split('px')[0];
                    const navpaddingRight = style.paddingRight.split('px')[0];
                    const navMarginLeft = style.marginLeft.split('px')[0];
                    const navMarginRight = style.marginRight.split('px')[0];
                    this.setState({
                        navWidthStandard: desktopNav['offsetWidth'],
                        navLeftPadding: Number(navPaddingLeft),
                        navRightPadding: Number(navpaddingRight),
                        navMarginLeft: Number(navMarginLeft),
                        navMarginRight: Number(navMarginRight)
                    });
                }
            }
        }
    }

    private isNavOutOfBounds(): boolean {
        if (MsDyn365.isBrowser && window) {
            if (this.state && this.state.navWidthStandard) {
                let actualWidth = 0;
                if (this.state.navWidthStandard >= screen.width) {
                    const navPaddingLeft = this.state.navLeftPadding ? this.state.navLeftPadding : 0;
                    const navpaddingRight = this.state.navRightPadding ? this.state.navRightPadding : 0;
                    const navMarginLeft = this.state.navMarginLeft ? this.state.navMarginLeft : 0;
                    const navMarginRight = this.state.navMarginRight ? this.state.navMarginRight : 0;
                    actualWidth = this.state.navWidthStandard - (navPaddingLeft + navpaddingRight + navMarginLeft + navMarginRight);
                    if (window.innerWidth > actualWidth + 20) {
                        return false;
                    }
                }
                if (window.innerWidth > this.state.navWidthStandard + 20) {
                    return false;
                }
                return true;
            }
            const navs = document.getElementsByTagName('nav');
            let desktopNav;
            if (navs) {
                for (let i = 0; i < navs.length; i++) {
                    if (
                        !this.hasClassInParent(navs[i], 'ms-header__mobile-hamburger-menu-body') &&
                        !desktopNav?.classList.contains('child') &&
                        !this.hasClassInParent(navs[i], 'ms-search-result-container') &&
                        !this.hasClassInParent(navs[i], 'pdp-breadcrumb') &&
                        !navs[i].classList.contains('basket-breadcrumb') &&
                        navs[i].classList.contains('ms-nav')
                    ) {
                        desktopNav = navs[i];
                    }
                }
            }
            if (desktopNav) {
                let offsetHeight = desktopNav['offsetHeight'] ? desktopNav['offsetHeight'] : 0;
                if (offsetHeight === 0) {
                    desktopNav.classList.remove('mobile-vp');
                    if (!desktopNav.classList.contains('desktop-vp')) {
                        desktopNav.classList.add('desktop-vp');
                    }
                    offsetHeight = desktopNav['offsetHeight'];
                    desktopNav.classList.remove('desktop-vp');
                    if (!desktopNav.classList.contains('mobile-vp')) {
                        desktopNav.classList.add('mobile-vp');
                    }
                }
                if (offsetHeight > 75) {
                    if (offsetHeight > 200) {
                        if (!desktopNav.classList.contains('mobile-vp')) {
                            desktopNav.classList.add('mobile-vp');
                        }
                    }
                    return true;
                }
            }
        }
        return false;
    }

    private _updateViewport(): void {
        this.viewport = this._getViewport();
        this.setState({ isMobile: this.isMobile });
    }

    private _getViewport(): GridSize {
        const { context } = this.props;

        // Always render in mobile viewport on a mobile device
        if (context.request && context.request.device && context.request.device.Type === 'Mobile') {
            return 'xs';
        }

        if (MsDyn365.isBrowser && window.innerWidth) {
            const gridSettings = context.request.gridSettings;
            if (gridSettings) {
                if (gridSettings.xs && window.innerWidth <= gridSettings.xs.w) {
                    return 'xs';
                } else if (gridSettings.sm && window.innerWidth <= gridSettings.sm.w) {
                    return 'sm';
                } else if (gridSettings.md && window.innerWidth <= gridSettings.md.w) {
                    return 'md';
                } else if (gridSettings.lg && window.innerWidth <= gridSettings.lg.w) {
                    return 'lg';
                }
                return 'xl';
            }
        }

        return 'xs';
    }
}

export default NavigationMenu;
